import { useState } from 'react';
import { API, ago, useApi } from '../lib/api.ts';
import { ensureSession } from '../lib/auth.ts';
import { Empty, ErrorState, Loading, Status, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';

interface Row { key: string; label: string; started: number; completed: number; partial: number; skipped: number; failed: number; playedMs: number; lastConfirmedPlay: string | null }

export function Reports() {
  const org = useOrg();
  const toast = useToast();
  const [groupBy, setGroupBy] = useState('day');
  const [days, setDays] = useState(7);
  const from = new Date(Date.now() - days * 86_400_000).toISOString();
  const q = `from=${encodeURIComponent(from)}&groupBy=${groupBy}`;
  const { data, error, loading, reload } = useApi<{ rows: Row[]; quarantinedEvents: number; note: string }>(orgPath(org, `/reports/playback?${q}`), [org.id, q]);
  const fleet = useApi<{ screens: { id: string; name: string; locationName: string; lastConfirmedPlay: string | null; desiredSequence: number; activeSequence: number | null; errors24h: number }[] }>(orgPath(org, '/reports/fleet'), [org.id]);
  async function exportCsv() {
    const res = await fetch(`${API}${orgPath(org, `/reports/playback.csv?from=${encodeURIComponent(from)}`)}`, { headers: { authorization: `Bearer ${(await ensureSession())?.accessToken}` } });
    if (!res.ok) return toast('Export failed', 'danger');
    const url = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement('a'), { href: url, download: `lumacast-playback-${days}d.csv` });
    a.click();
    URL.revokeObjectURL(url);
  }
  const totals = data?.rows.reduce((t, r) => ({ started: t.started + r.started, completed: t.completed + r.completed, partial: t.partial + r.partial, skipped: t.skipped + r.skipped, failed: t.failed + r.failed }), { started: 0, completed: 0, partial: 0, skipped: 0, failed: 0 });
  return (
    <>
      <div className="page-head">
        <div><h1>Reports</h1><p>Proof of play from player event journals. Offline players report when they reconnect; replays never double-count.</p></div>
        <div className="row">
          <div className="segmented" role="group" aria-label="Period">{[1, 7, 30].map((d) => <button key={d} aria-pressed={days === d} onClick={() => setDays(d)}>{d === 1 ? '24 h' : `${d} days`}</button>)}</div>
          {can(org, 'reports.export') && <button className="btn" onClick={() => void exportCsv()}>Export CSV</button>}
        </div>
      </div>
      {error && <ErrorState error={error} retry={reload} />}
      {totals && (
        <div className="grid grid-stats" style={{ marginBottom: 16 }}>
          {Object.entries(totals).map(([k, v]) => <div key={k} className="card stat"><div className="value">{v}</div><div className="label">{k}</div></div>)}
        </div>
      )}
      <div className="card">
        <div className="card-head">
          <h2>Playback</h2>
          <div className="segmented" role="group" aria-label="Group by">{['day', 'location', 'screen', 'campaign', 'asset'].map((g) => <button key={g} aria-pressed={groupBy === g} onClick={() => setGroupBy(g)}>{g}</button>)}</div>
        </div>
        {loading && !data ? <div className="card-pad"><Loading /></div> : !data?.rows.length ? <Empty title="No playback reported in this period" /> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{groupBy}</th><th>Started</th><th>Completed ≥95%</th><th>Partial</th><th>Skipped</th><th>Errors</th><th>Played</th><th>Last confirmed</th></tr></thead>
              <tbody>{data.rows.map((r) => <tr key={r.key}><td>{r.label}</td><td>{r.started}</td><td>{r.completed}</td><td>{r.partial}</td><td>{r.skipped}</td><td>{r.failed}</td><td>{Math.round(r.playedMs / 60000)} min</td><td className="subtle">{ago(r.lastConfirmedPlay)}</td></tr>)}</tbody>
            </table>
          </div>
        )}
        {data && <p className="subtle card-pad" style={{ paddingTop: 8 }}>{data.note}{data.quarantinedEvents ? ` ${data.quarantinedEvents} implausible event(s) were quarantined.` : ''}</p>}
      </div>
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><h2>Fleet coverage</h2></div>
        {fleet.data && (
          <div className="table-wrap"><table className="table">
            <thead><tr><th>Screen</th><th>Location</th><th>Last confirmed play</th><th>Release</th><th>Errors (24h)</th></tr></thead>
            <tbody>{fleet.data.screens.map((s) => <tr key={s.id}><td>{s.name}</td><td className="muted">{s.locationName}</td><td>{ago(s.lastConfirmedPlay)}</td><td className="mono">{s.activeSequence ?? '—'} / {s.desiredSequence || '—'}</td><td>{s.errors24h ? <Status value="warning" label={String(s.errors24h)} /> : 0}</td></tr>)}</tbody>
          </table></div>
        )}
      </div>
    </>
  );
}
